import { useState, useMemo, useCallback } from 'react';

interface CalendarDay {
  key: string
  day: number | null
}

const useCalendar = () => {
  const todayString = useMemo(() => new Date().toISOString().split('T')[0], []);
  const [calendarYear, setCalendarYear] = useState(() => new Date().getFullYear());
  const [calendarMonth, setCalendarMonth] = useState(() => new Date().getMonth());
  const [selectedDate, setSelectedDate] = useState('');

  const calendarDays: CalendarDay[] = useMemo(() => {
    const firstDayOfWeek = new Date(calendarYear, calendarMonth, 1).getDay();
    const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate();
    const cells: CalendarDay[] = [];
    for (let i = 0; i < firstDayOfWeek; i += 1) {
      cells.push({ key: `pad-${i}`, day: null });
    }
    for (let d = 1; d <= daysInMonth; d += 1) {
      cells.push({ key: `day-${d}`, day: d });
    }
    return cells;
  }, [calendarYear, calendarMonth]);

  const getDayKey = useCallback(
    (day: number | null) => (day
      ? `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      : ''),
    [calendarYear, calendarMonth],
  );

  const isAvailableDay = useCallback(
    (day: number | null, availableDates: string[]) => !!day && availableDates.includes(getDayKey(day)),
    [getDayKey],
  );

  const isSelectedDay = useCallback(
    (day: number | null) => !!day && selectedDate === getDayKey(day),
    [selectedDate, getDayKey],
  );

  const isPast = useCallback(
    (day: number | null) => {
      if (!day) return true;
      return getDayKey(day) < todayString;
    },
    [getDayKey, todayString],
  );

  const handleSelectDay = useCallback(
    (day: number | null, availableDates: string[]) => {
      if (!isAvailableDay(day, availableDates)) return;
      setSelectedDate(getDayKey(day));
    },
    [isAvailableDay, getDayKey],
  );

  const handlePreviousMonth = useCallback(() => {
    if (calendarMonth === 0) {
      setCalendarMonth(11);
      setCalendarYear((y) => y - 1);
    } else {
      setCalendarMonth((m) => m - 1);
    }
  }, [calendarMonth]);

  const handleNextMonth = useCallback(() => {
    if (calendarMonth === 11) {
      setCalendarMonth(0);
      setCalendarYear((y) => y + 1);
    } else {
      setCalendarMonth((m) => m + 1);
    }
  }, [calendarMonth]);

  const currentMonthString = `${calendarYear}${String(calendarMonth + 1).padStart(2, '0')}`;

  return {
    calendarYear,
    calendarMonth,
    calendarDays,
    selectedDate,
    setSelectedDate,
    currentMonthString,
    getDayKey,
    isAvailableDay,
    isSelectedDay,
    isPast,
    handleSelectDay,
    handlePreviousMonth,
    handleNextMonth,
  };
};

export default useCalendar;
